import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ArrowLeft, Plus, Trash2, FileText, Calendar, MapPin, User, Car, Circle, Loader2 } from 'lucide-react';
import { API } from '../config/api';
import { AUTODROMOS } from '../lib/autodromos';
import TandaForm from './TandaForm';
import TireInventory from './TireInventory';
import EventSummary from './EventSummary';
import OfflineBanner from './OfflineBanner';
import { enqueue as enqueueOffline } from '../lib/pitOfflineQueue';

const TANDA_LABELS = {
  entrenamiento_1: 'Entrenamiento 1',
  entrenamiento_2: 'Entrenamiento 2',
  clasificacion_1: 'Clasificación 1',
  clasificacion_2: 'Clasificación 2',
  serie: 'Serie',
  final: 'Final',
};

const TANDA_ORDER = ['entrenamiento_1', 'entrenamiento_2', 'clasificacion_1', 'clasificacion_2', 'serie', 'final'];

// Test/practice day: 12 outings, no quali / no race.
const SALIDA_ORDER = Array.from({ length: 12 }, (_, i) => `salida_${i + 1}`);
const SALIDA_LABELS = SALIDA_ORDER.reduce((acc, k, i) => {
  acc[k] = `Salida ${i + 1}`;
  return acc;
}, {});

const tandaOrderFor = (eventType) => (eventType === 'test' ? SALIDA_ORDER : TANDA_ORDER);
const tandaLabelsFor = (eventType) => (eventType === 'test' ? SALIDA_LABELS : TANDA_LABELS);

const CATEGORY_LABELS = {
  turismo_pista: 'Turismo Pista',
  carshow_santafesino: 'CarShow',
  turismo_nacional: 'Turismo Nacional',
  simracing: 'Simracing',
  rotax: 'ROTAX Karting',
};

export default function PitEngineer() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeTanda, setActiveTanda] = useState('entrenamiento_1');
  const [mainTab, setMainTab] = useState('events'); // events | tires
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [pilotOptions, setPilotOptions] = useState([]);
  const [newPilotMode, setNewPilotMode] = useState(false);  // true = typing a new pilot
  const [newEvent, setNewEvent] = useState({
    category: 'turismo_pista',
    circuit: '',                                           // dropdown value or "__other__"
    circuit_custom: '',
    pilot: '',
    car: '',
    event_type: 'weekend',                                 // weekend | test
    date: new Date().toISOString().slice(0, 10),
    base_car_weight: '',
    regulatory_weight: '',
    regulatory_ballast: '',
  });

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pit/events`, { withCredentials: true });
      setEvents(res.data.events || []);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Inicia sesión para continuar');
        navigate('/login');
      } else if (err.response?.status === 403) {
        toast.error('Plan PRO requerido para Ingeniería de Pista');
        navigate('/app');
      } else {
        toast.error('Error al cargar los eventos');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPilots = async () => {
    try {
      const res = await axios.get(`${API}/pit/pilots`, { withCredentials: true });
      setPilotOptions(res.data.pilots || []);
    } catch {
      // non-blocking
    }
  };

  const openNewEventDialog = () => {
    loadPilots();
    setNewPilotMode(false);
    setShowNewDialog(true);
  };

  const openEvent = async (eventId) => {
    try {
      const res = await axios.get(`${API}/pit/events/${eventId}`, { withCredentials: true });
      setSelectedEvent(res.data);
      const order = tandaOrderFor(res.data.event_type);
      setActiveTanda(order[0]);
    } catch (err) {
      toast.error('No se pudo cargar el evento');
    }
  };

  const createEvent = async () => {
    const circuitFinal = newEvent.circuit === '__other__'
      ? (newEvent.circuit_custom || '').trim()
      : (newEvent.circuit || '');
    if (!circuitFinal || !newEvent.pilot || !newEvent.car) {
      toast.error('Completá categoría, autódromo, piloto y auto');
      return;
    }
    try {
      const payload = {
        category: newEvent.category,
        circuit: circuitFinal,
        pilot: newEvent.pilot,
        car: newEvent.car,
        event_type: newEvent.event_type,
        date: newEvent.date,
      };
      if (newEvent.base_car_weight !== '' && newEvent.base_car_weight != null) {
        payload.base_car_weight = Number(newEvent.base_car_weight);
      }
      if (newEvent.regulatory_weight !== '' && newEvent.regulatory_weight != null) {
        payload.regulatory_weight = Number(newEvent.regulatory_weight);
      }
      if (newEvent.regulatory_ballast !== '' && newEvent.regulatory_ballast != null) {
        payload.regulatory_ballast = Number(newEvent.regulatory_ballast);
      }
      const res = await axios.post(`${API}/pit/events`, payload, { withCredentials: true });
      toast.success('Evento creado');
      setShowNewDialog(false);
      setNewEvent({
        ...newEvent,
        circuit: '',
        circuit_custom: '',
        pilot: '',
        car: '',
        base_car_weight: '',
        regulatory_weight: '',
        regulatory_ballast: '',
      });
      await loadEvents();
      setSelectedEvent(res.data.event);
    } catch (err) {
      toast.error('Error al crear el evento');
    }
  };

  const deleteEvent = async (eventId, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar este fin de semana? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/pit/events/${eventId}`, { withCredentials: true });
      toast.success('Evento eliminado');
      setEvents(events.filter(e2 => e2.event_id !== eventId));
      if (selectedEvent?.event_id === eventId) setSelectedEvent(null);
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const saveTanda = async (tandaKey, tandaData) => {
    if (!selectedEvent) return;
    const path = `/pit/events/${selectedEvent.event_id}`;
    const body = { tandas: { [tandaKey]: tandaData } };
    // Optimistic update so the UI shows the saved data immediately, even
    // before the server confirms (or while offline).
    const optimistic = {
      ...selectedEvent,
      tandas: {
        ...(selectedEvent.tandas || {}),
        [tandaKey]: { ...(selectedEvent.tandas?.[tandaKey] || {}), ...tandaData, saved_at: new Date().toISOString() },
      },
    };
    setSelectedEvent(optimistic);

    try {
      const res = await axios.patch(`${API}${path}`, body, { withCredentials: true });
      setSelectedEvent(res.data.event);
      toast.success(`Tanda guardada`, { duration: 1500 });
    } catch (err) {
      const isNetwork = !err.response || !navigator.onLine;
      if (isNetwork) {
        enqueueOffline({ method: 'PATCH', path, data: body });
        toast.warning('Sin conexión — guardado local, se sincroniza al volver el WiFi', { duration: 3000 });
      } else {
        toast.error('Error al guardar la tanda');
      }
    }
  };

  // Event detail view
  if (selectedEvent) {
    const eventType = selectedEvent.event_type || 'weekend';
    const order = tandaOrderFor(eventType);
    const labels = tandaLabelsFor(eventType);
    const isTest = eventType === 'test';

    const updateBaseWeight = async (kg) => {
      const val = kg === '' || kg == null ? null : Number(kg);
      const path = `/pit/events/${selectedEvent.event_id}`;
      const body = { base_car_weight: val };
      // Optimistic update
      setSelectedEvent({ ...selectedEvent, base_car_weight: val });
      try {
        const res = await axios.patch(`${API}${path}`, body, { withCredentials: true });
        setSelectedEvent(res.data.event);
      } catch (err) {
        const isNetwork = !err.response || !navigator.onLine;
        if (isNetwork) {
          enqueueOffline({ method: 'PATCH', path, data: body });
          toast.warning('Sin conexión — peso base guardado local', { duration: 2500 });
        } else {
          toast.error('No se pudo guardar el peso base');
        }
      }
    };

    const updateEventField = async (field, raw) => {
      const val = raw === '' || raw == null ? null : Number(raw);
      const path = `/pit/events/${selectedEvent.event_id}`;
      const body = { [field]: val };
      setSelectedEvent({ ...selectedEvent, [field]: val });
      try {
        const res = await axios.patch(`${API}${path}`, body, { withCredentials: true });
        setSelectedEvent(res.data.event);
      } catch (err) {
        const isNetwork = !err.response || !navigator.onLine;
        if (isNetwork) {
          enqueueOffline({ method: 'PATCH', path, data: body });
          toast.warning('Sin conexión — guardado local', { duration: 2500 });
        } else {
          toast.error(`No se pudo guardar ${field}`);
        }
      }
    };

    return (
      <div className="min-h-screen bg-neutral-950 text-white">
        <OfflineBanner />
        <header className="sticky top-0 z-40 bg-neutral-950/95 backdrop-blur border-b border-neutral-800">
          <div className="container mx-auto px-4 py-3 flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedEvent(null)}
              data-testid="pit-back-to-list"
              className="text-gray-400 hover:text-white h-10"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Volver
            </Button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-white truncate flex items-center gap-2">
                <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                {selectedEvent.circuit} — {selectedEvent.pilot}
                {isTest && (
                  <Badge className="bg-blue-700 text-white text-[10px] h-5 ml-1">Entrenamiento Libre</Badge>
                )}
              </h1>
              <div className="text-xs text-gray-500 flex items-center gap-3 flex-wrap">
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{selectedEvent.date}</span>
                <span className="flex items-center gap-1"><Car className="w-3 h-3" />{selectedEvent.car}</span>
                {selectedEvent.category && (
                  <Badge variant="outline" className="text-[10px] h-4 px-1.5 text-gray-300 border-gray-600">{CATEGORY_LABELS[selectedEvent.category] || selectedEvent.category}</Badge>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap justify-end" data-testid="event-weights-header">
              <div className="flex flex-col items-end">
                <Label className="text-[10px] text-gray-500 leading-none">Peso base</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  placeholder="kg"
                  defaultValue={selectedEvent.base_car_weight ?? ''}
                  onBlur={(e) => {
                    const v = e.target.value;
                    if (v !== String(selectedEvent.base_car_weight ?? '')) updateBaseWeight(v);
                  }}
                  data-testid="base-car-weight-input"
                  className="w-20 h-8 mt-0.5 text-sm bg-neutral-800 border-neutral-700 text-white text-right font-mono"
                />
              </div>
              <div className="flex flex-col items-end">
                <Label className="text-[10px] text-gray-500 leading-none">Reglamentario</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  placeholder="kg"
                  defaultValue={selectedEvent.regulatory_weight ?? ''}
                  onBlur={(e) => {
                    const v = e.target.value;
                    if (v !== String(selectedEvent.regulatory_weight ?? '')) updateEventField('regulatory_weight', v);
                  }}
                  data-testid="regulatory-weight-input"
                  className="w-20 h-8 mt-0.5 text-sm bg-neutral-800 border-neutral-700 text-white text-right font-mono"
                />
              </div>
              <div className="flex flex-col items-end">
                <Label className="text-[10px] text-gray-500 leading-none">Lastre</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  placeholder="kg"
                  defaultValue={selectedEvent.regulatory_ballast ?? ''}
                  onBlur={(e) => {
                    const v = e.target.value;
                    if (v !== String(selectedEvent.regulatory_ballast ?? '')) updateEventField('regulatory_ballast', v);
                  }}
                  data-testid="regulatory-ballast-input"
                  className="w-20 h-8 mt-0.5 text-sm bg-neutral-800 border-neutral-700 text-white text-right font-mono"
                />
              </div>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-3 md:px-6 py-4">
          <Tabs value={activeTanda} onValueChange={setActiveTanda}>
            <TabsList className={
              isTest
                ? 'flex flex-wrap h-auto bg-neutral-900 border border-neutral-800 gap-1 p-1'
                : 'grid grid-cols-4 md:grid-cols-7 h-auto bg-neutral-900 border border-neutral-800 gap-1 p-1'
            }>
              {order.map((t) => (
                <TabsTrigger
                  key={t}
                  value={t}
                  data-testid={`tanda-tab-${t}`}
                  className={
                    isTest
                      ? 'text-xs data-[state=active]:bg-red-600 data-[state=active]:text-white py-2.5 flex-1 min-w-[80px]'
                      : 'text-xs data-[state=active]:bg-red-600 data-[state=active]:text-white py-2.5'
                  }
                >
                  {labels[t]}
                </TabsTrigger>
              ))}
              <TabsTrigger
                value="resumen"
                data-testid="tanda-tab-resumen"
                className={
                  isTest
                    ? 'text-xs data-[state=active]:bg-yellow-600 data-[state=active]:text-white py-2.5 font-semibold flex-1 min-w-[80px]'
                    : 'text-xs data-[state=active]:bg-yellow-600 data-[state=active]:text-white py-2.5 font-semibold'
                }
              >
                Resumen
              </TabsTrigger>
            </TabsList>

            {order.map((t, idx) => {
              const prevKey = idx > 0 ? order[idx - 1] : null;
              const prevTanda = prevKey ? (selectedEvent.tandas?.[prevKey] || null) : null;
              const prevLabel = prevKey ? labels[prevKey] : null;
              return (
                <TabsContent key={t} value={t} className="mt-4">
                  <TandaForm
                    tandaKey={t}
                    tandaLabel={labels[t]}
                    tandaData={selectedEvent.tandas?.[t] || {}}
                    onSave={(data) => saveTanda(t, data)}
                    baseCarWeight={selectedEvent.base_car_weight}
                    regulatoryWeight={selectedEvent.regulatory_weight}
                    regulatoryBallast={selectedEvent.regulatory_ballast}
                    hideRefTime={isTest}
                    eventId={selectedEvent.event_id}
                    eventDate={selectedEvent.date}
                    eventCategory={selectedEvent.category}
                    eventCircuit={selectedEvent.circuit}
                    pilotName={selectedEvent.pilot}
                    previousTandaData={prevTanda}
                    previousTandaLabel={prevLabel}
                  />
                </TabsContent>
              );
            })}
            <TabsContent value="resumen" className="mt-4">
              <EventSummary event={selectedEvent} />
            </TabsContent>
          </Tabs>
        </main>
      </div>
    );
  }

  // Events list view
  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <OfflineBanner />
      <header className="sticky top-0 z-40 bg-neutral-950/95 backdrop-blur border-b border-neutral-800">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/app')}
              data-testid="pit-back-to-dashboard"
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Volver
            </Button>
            <div>
              <h1 className="text-lg font-bold text-white flex items-center gap-2">
                <FileText className="w-4 h-4 text-red-500" />
                Ingeniería de Pista
              </h1>
              <p className="text-xs text-gray-500">Planilla digital de fin de semana</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-3 md:px-6 py-6 max-w-5xl">
        <Tabs value={mainTab} onValueChange={setMainTab}>
          <TabsList className="bg-neutral-900 border border-neutral-800">
            <TabsTrigger value="events" data-testid="tab-events">Fines de Semana</TabsTrigger>
            <TabsTrigger value="tires" data-testid="tab-tires">Inventario de Cubiertas</TabsTrigger>
          </TabsList>

          <TabsContent value="events" className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold">Eventos</h2>
                <p className="text-xs text-gray-500">Fines de semana (6 tandas) o entrenamientos libres (12 salidas)</p>
              </div>
              <Button
                onClick={openNewEventDialog}
                data-testid="new-event-btn"
                className="bg-red-600 hover:bg-red-700"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Nuevo Evento
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Cargando...
              </div>
            ) : events.length === 0 ? (
              <Card className="bg-neutral-900 border-neutral-800">
                <CardContent className="py-16 text-center text-gray-500">
                  <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p className="mb-2">Todavía no cargaste ningún fin de semana</p>
                  <p className="text-xs">Tocá "Nuevo Fin de Semana" para crear el primero</p>
                </CardContent>
              </Card>
            ) : (
              (() => {
                // Group events: Date → Category → list of events (one per pilot).
                // Dates sorted descending so the latest weekend appears first,
                // and within each date the categories are alphabetical.
                const dates = {};
                for (const ev of events) {
                  const dateKey = ev.date || 'Sin fecha';
                  if (!dates[dateKey]) dates[dateKey] = {};
                  const cat = ev.category || 'sin_categoria';
                  if (!dates[dateKey][cat]) dates[dateKey][cat] = [];
                  dates[dateKey][cat].push(ev);
                }
                const orderedDates = Object.keys(dates).sort().reverse();
                const fmtDate = (d) => {
                  if (!d || d === 'Sin fecha') return 'Sin fecha';
                  const m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
                  if (!m) return d;
                  return `${m[3]}/${m[2]}/${m[1]}`;
                };
                return (
                  <div className="space-y-5">
                    {orderedDates.map((dateKey) => {
                      const cats = dates[dateKey];
                      const cnt = Object.values(cats).flat().length;
                      const orderedCats = Object.keys(cats).sort();
                      return (
                        <div key={dateKey} data-testid={`events-group-${dateKey}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-red-700 text-white text-xs flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {fmtDate(dateKey)}
                            </Badge>
                            <div className="text-xs text-gray-600">
                              {cnt} {cnt === 1 ? 'evento' : 'eventos'}
                            </div>
                          </div>
                          <div className="space-y-3">
                            {orderedCats.map((cat) => (
                              <div key={cat}>
                                <div className="text-[11px] uppercase text-gray-500 font-semibold mb-1.5 ml-1">
                                  {CATEGORY_LABELS[cat] || (cat === 'sin_categoria' ? 'Sin categoría' : cat)}
                                </div>
                                <div className="grid gap-2">
                                  {cats[cat].map((e) => (
                                    <Card
                                      key={e.event_id}
                                      onClick={() => openEvent(e.event_id)}
                                      data-testid={`event-card-${e.event_id}`}
                                      className="bg-neutral-900 border-neutral-800 hover:border-red-600/50 transition cursor-pointer"
                                    >
                                      <CardContent className="p-3 flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border ${
                                          e.event_type === 'test'
                                            ? 'bg-blue-700/10 border-blue-700/30'
                                            : 'bg-red-600/10 border-red-600/30'
                                        }`}>
                                          <User className={`w-4 h-4 ${e.event_type === 'test' ? 'text-blue-400' : 'text-red-500'}`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <h3 className="font-semibold text-white truncate text-sm flex items-center gap-2">
                                            <span className="truncate">
                                              {(CATEGORY_LABELS[e.category] || e.category || 'Sin categoría')}
                                              {' / '}{e.circuit}
                                              {' / '}{e.pilot}
                                            </span>
                                            {e.event_type === 'test' && (
                                              <Badge className="bg-blue-700 text-white text-[10px] h-4 px-1.5 flex-shrink-0">Libre</Badge>
                                            )}
                                          </h3>
                                          <div className="text-xs text-gray-500 flex items-center gap-3 flex-wrap">
                                            <span className="flex items-center gap-1"><Car className="w-3 h-3" />{e.car}</span>
                                          </div>
                                        </div>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={(ev) => deleteEvent(e.event_id, ev)}
                                          data-testid={`delete-event-${e.event_id}`}
                                          className="text-gray-600 hover:text-red-500"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </CardContent>
                                    </Card>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            )}
          </TabsContent>

          <TabsContent value="tires" className="mt-4">
            <TireInventory />
          </TabsContent>
        </Tabs>
      </main>

      {/* New Event Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="bg-neutral-900 border-neutral-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Nuevo Evento</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Categoría → Autódromo → Piloto
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2 max-h-[75vh] overflow-y-auto">
            {/* 1. Categoría */}
            <div>
              <Label className="text-xs text-gray-400">Categoría</Label>
              <select
                value={newEvent.category}
                onChange={(e) => setNewEvent({ ...newEvent, category: e.target.value })}
                data-testid="new-event-category"
                className="w-full bg-neutral-800 border border-neutral-700 text-white rounded-md px-3 py-2 text-sm h-10"
              >
                <option value="turismo_pista">Turismo Pista</option>
                <option value="carshow_santafesino">CarShow Santafesino</option>
                <option value="turismo_nacional">Turismo Nacional</option>
                <option value="rotax">ROTAX Karting</option>
                <option value="simracing">Simracing</option>
                <option value="">Otra</option>
              </select>
            </div>

            {/* 2. Tipo de evento */}
            <div>
              <Label className="text-xs text-gray-400">Tipo de evento</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setNewEvent({ ...newEvent, event_type: 'weekend' })}
                  data-testid="event-type-weekend"
                  className={`text-xs px-3 py-2 rounded-md border transition ${
                    newEvent.event_type === 'weekend'
                      ? 'bg-red-600 border-red-600 text-white'
                      : 'bg-neutral-800 border-neutral-700 text-gray-300 hover:border-neutral-600'
                  }`}
                >
                  Fin de semana<br /><span className="text-[10px] opacity-80">6 tandas</span>
                </button>
                <button
                  type="button"
                  onClick={() => setNewEvent({ ...newEvent, event_type: 'test' })}
                  data-testid="event-type-test"
                  className={`text-xs px-3 py-2 rounded-md border transition ${
                    newEvent.event_type === 'test'
                      ? 'bg-blue-700 border-blue-700 text-white'
                      : 'bg-neutral-800 border-neutral-700 text-gray-300 hover:border-neutral-600'
                  }`}
                >
                  Entrenamiento Libre<br /><span className="text-[10px] opacity-80">12 salidas</span>
                </button>
              </div>
            </div>

            {/* 3. Autódromo (dropdown + Otro) */}
            <div>
              <Label className="text-xs text-gray-400 flex items-center gap-1"><MapPin className="w-3 h-3" />Autódromo / Pista</Label>
              <select
                value={newEvent.circuit}
                onChange={(e) => setNewEvent({ ...newEvent, circuit: e.target.value })}
                data-testid="new-event-circuit-select"
                className="w-full bg-neutral-800 border border-neutral-700 text-white rounded-md px-3 py-2 text-sm h-10"
              >
                <option value="">— Seleccioná un autódromo —</option>
                {AUTODROMOS.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
                <option value="__other__">Otro...</option>
              </select>
              {newEvent.circuit === '__other__' && (
                <Input
                  value={newEvent.circuit_custom}
                  onChange={(e) => setNewEvent({ ...newEvent, circuit_custom: e.target.value })}
                  placeholder="Nombre del autódromo"
                  data-testid="new-event-circuit-custom"
                  className="bg-neutral-800 border-neutral-700 text-white mt-1.5"
                />
              )}
            </div>

            {/* 4. Piloto */}
            <div>
              <Label className="text-xs text-gray-400 flex items-center gap-1"><User className="w-3 h-3" />Piloto</Label>
              {pilotOptions.length > 0 && !newPilotMode ? (
                <div className="flex gap-1.5">
                  <select
                    value={newEvent.pilot}
                    onChange={(e) => {
                      if (e.target.value === '__new__') {
                        setNewPilotMode(true);
                        setNewEvent({ ...newEvent, pilot: '' });
                      } else {
                        setNewEvent({ ...newEvent, pilot: e.target.value });
                      }
                    }}
                    data-testid="new-event-pilot-select"
                    className="flex-1 bg-neutral-800 border border-neutral-700 text-white rounded-md px-3 py-2 text-sm h-10"
                  >
                    <option value="">Elegí un piloto...</option>
                    {pilotOptions.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                    <option value="__new__">+ Nuevo piloto...</option>
                  </select>
                </div>
              ) : (
                <div className="flex gap-1.5">
                  <Input
                    value={newEvent.pilot}
                    onChange={(e) => setNewEvent({ ...newEvent, pilot: e.target.value })}
                    placeholder="Lucio Rodriguez"
                    data-testid="new-event-pilot"
                    className="bg-neutral-800 border-neutral-700 text-white flex-1"
                  />
                  {pilotOptions.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      onClick={() => { setNewPilotMode(false); setNewEvent({ ...newEvent, pilot: '' }); }}
                      className="text-xs"
                    >
                      Lista
                    </Button>
                  )}
                </div>
              )}
              {pilotOptions.length > 0 && (
                <p className="text-[10px] text-gray-500 mt-1">
                  Elegí un piloto del inventario para que las cubiertas linkeen automáticamente.
                </p>
              )}
            </div>

            {/* 5. Auto */}
            <div>
              <Label className="text-xs text-gray-400 flex items-center gap-1"><Car className="w-3 h-3" />Auto</Label>
              <Input
                value={newEvent.car}
                onChange={(e) => setNewEvent({ ...newEvent, car: e.target.value })}
                placeholder="Fiat 147 TP #42"
                data-testid="new-event-car"
                className="bg-neutral-800 border-neutral-700 text-white"
              />
            </div>

            {/* 6. Fecha */}
            <div>
              <Label className="text-xs text-gray-400">Fecha</Label>
              <Input
                type="date"
                value={newEvent.date}
                onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                data-testid="new-event-date"
                className="bg-neutral-800 border-neutral-700 text-white"
              />
            </div>

            {/* 7. Peso base */}
            <div>
              <Label className="text-xs text-gray-400 flex items-center gap-1">Peso base del auto (kg)</Label>
              <Input
                type="number"
                inputMode="decimal"
                step="0.1"
                value={newEvent.base_car_weight}
                onChange={(e) => setNewEvent({ ...newEvent, base_car_weight: e.target.value })}
                placeholder="Opcional. Ej: 950"
                data-testid="new-event-base-weight"
                className="bg-neutral-800 border-neutral-700 text-white"
              />
              <p className="text-[10px] text-gray-500 mt-1">
                Peso del auto + piloto sin combustible. Se usa para calcular peso total (+ combustible × 0.74 kg/L).
              </p>
            </div>

            {/* 8. Peso reglamentario + Lastre */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-gray-400">Peso reglamentario (kg)</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={newEvent.regulatory_weight}
                  onChange={(e) => setNewEvent({ ...newEvent, regulatory_weight: e.target.value })}
                  placeholder="Ej: 1050"
                  data-testid="new-event-regulatory-weight"
                  className="bg-neutral-800 border-neutral-700 text-white"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-400">Lastre reglamentario (kg)</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={newEvent.regulatory_ballast}
                  onChange={(e) => setNewEvent({ ...newEvent, regulatory_ballast: e.target.value })}
                  placeholder="Ej: 25"
                  data-testid="new-event-regulatory-ballast"
                  className="bg-neutral-800 border-neutral-700 text-white"
                />
              </div>
              <p className="col-span-2 text-[10px] text-gray-500 -mt-1">
                Mínimo que exige la categoría y el lastre que cargás para cumplirlo. Se pueden sobrescribir por tanda.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewDialog(false)}>Cancelar</Button>
            <Button onClick={createEvent} data-testid="create-event-submit" className="bg-red-600 hover:bg-red-700">
              Crear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
